/**
 * Age distribution of SomeBuddy help requests, Finland, as a percentage share
 * of that year's cases.
 *
 * Lives here rather than inside the annual report page because two pages now
 * draw the same histogram: the report itself
 * (`src/pages/schools/annual-reports/2025.tsx`) and the UK schools section
 * (`components/organisms/schools/uk/AgeDistribution.tsx`). When the 2026
 * figures land, this file is the only edit — the report page reads it through
 * `ANNUAL_REPORT_2025_STATIC`, and both year pickers are built from
 * `Object.keys()`, so a new year appears on both pages at once.
 *
 * The numbers are Finnish case data. Any page that renders them has to say so:
 * see the source note on the UK section.
 *
 * `fill` is baked into each row because Recharts colours a bar from its own
 * datum via `<Cell>`. Age 13 is the light green — it is the median age of a
 * help seeker in every year here, and the point the surrounding copy makes.
 */

/** One bar: an age in years and its share of that year's cases. */
export type AgeDistributionDatum = {
  age: number
  cases: number
  fill: string
}

/** Keyed by calendar year. `Object.keys()` gives the year picker its buttons. */
export type AgeDistributionByYear = Record<string, AgeDistributionDatum[]>

const BAR = "#1a5f4a" // kkm-darkgreen
const BAR_MEDIAN = "#4db89a" // the median-age bar, lifted out of the run

export const ageDistributionByYear: AgeDistributionByYear = {
  2023: [
    { age: 6, cases: 0.3, fill: BAR },
    { age: 7, cases: 1.9, fill: BAR },
    { age: 8, cases: 2.9, fill: BAR },
    { age: 9, cases: 5.7, fill: BAR },
    { age: 10, cases: 11.1, fill: BAR },
    { age: 11, cases: 10.3, fill: BAR },
    { age: 12, cases: 13.5, fill: BAR },
    { age: 13, cases: 19.5, fill: BAR_MEDIAN },
    { age: 14, cases: 17.7, fill: BAR },
    { age: 15, cases: 12.1, fill: BAR },
    { age: 16, cases: 4.2, fill: BAR },
    { age: 17, cases: 0.8, fill: BAR },
  ],
  2024: [
    { age: 6, cases: 0.7, fill: BAR },
    { age: 7, cases: 1.0, fill: BAR },
    { age: 8, cases: 4.2, fill: BAR },
    { age: 9, cases: 8.0, fill: BAR },
    { age: 10, cases: 12.7, fill: BAR },
    { age: 11, cases: 13.2, fill: BAR },
    { age: 12, cases: 16.0, fill: BAR },
    { age: 13, cases: 16.3, fill: BAR_MEDIAN },
    { age: 14, cases: 13.4, fill: BAR },
    { age: 15, cases: 9.7, fill: BAR },
    { age: 16, cases: 3.5, fill: BAR },
    { age: 17, cases: 1.4, fill: BAR },
  ],
  2025: [
    { age: 6, cases: 0.9, fill: BAR },
    { age: 7, cases: 1.3, fill: BAR },
    { age: 8, cases: 0.7, fill: BAR },
    { age: 9, cases: 5.1, fill: BAR },
    { age: 10, cases: 10.3, fill: BAR },
    { age: 11, cases: 13.9, fill: BAR },
    { age: 12, cases: 17.2, fill: BAR },
    { age: 13, cases: 18.9, fill: BAR_MEDIAN },
    { age: 14, cases: 14.1, fill: BAR },
    { age: 15, cases: 9.6, fill: BAR },
    { age: 16, cases: 6.4, fill: BAR },
    { age: 17, cases: 1.5, fill: BAR },
  ],
}

export default ageDistributionByYear
